import React, { useEffect, useState } from 'react';
import apiService from '../apiService';

export default function PendingWalletTransactions() {
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const resp = await apiService.getPendingWalletTransactions(p, perPage);
      if (resp?.success) {
        const data = resp.data?.transactions ?? resp.data;
        setTransactions(data.data || []);
        setPage(data.current_page || p);
      } else {
        alert(resp?.data?.message || 'Failed to load');
      }
    } catch (e) {
      console.error(e);
      alert('Failed to load pending transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, []);

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const performBulk = async (action) => {
    if (!selected.size) return alert('Select at least one transaction');
    if (!window.confirm(`Confirm ${action} for ${selected.size} transactions?`)) return;
    try {
      const resp = await apiService.bulkAdminTransactionAction(Array.from(selected), action);
      if (resp?.success) {
        alert('Bulk action completed');
        setSelected(new Set());
        load(1);
      } else {
        alert(resp?.data?.message || 'Bulk action failed');
      }
    } catch (e) {
      console.error(e);
      alert('Bulk action failed');
    }
  };

  return (
    <div className="px-4 py-6 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Pending Wallet Transactions</h2>
          <p className="text-sm text-gray-500 mt-1">
            Review and approve or reject pending wallet top-ups in a single place.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => performBulk('approve')}
            disabled={loading || selected.size === 0}
            className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Approve Selected
          </button>
          <button
            onClick={() => performBulk('reject')}
            disabled={loading || selected.size === 0}
            className="inline-flex items-center justify-center rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reject Selected
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <span className="text-sm text-gray-600">
          {selected.size > 0 ? `${selected.size} transaction(s) selected` : `${transactions.length} pending transaction(s)`}
        </span>
        <span className="text-sm text-gray-500">Last refreshed at {new Date().toLocaleString()}</span>
      </div>

      <div className="overflow-x-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-6 text-center text-gray-600">Loading pending transactions...</div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No pending wallet transactions available right now.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 w-[40px]"></th>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Reference</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transactions.map(tx => (
                <tr key={tx.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(tx.id)}
                      onChange={() => toggleSelect(tx.id)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800">{tx.id}</td>
                  <td className="px-4 py-3 text-gray-700">
                    <div className="truncate max-w-[180px]">{tx.user?.name || tx.user?.email || tx.user_id}</div>
                    {tx.user?.mobile && <div className="text-xs text-gray-400">{tx.user.mobile}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-900">₹{parseFloat(tx.amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {tx.meta?.utr || tx.meta?.gateway || tx.reference_type || tx.reference || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                        tx.status === 'pending_verification'
                          ? 'bg-yellow-100 text-yellow-800'
                          : tx.status === 'approved'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {tx.status?.replace('_', ' ') || 'Unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{new Date(tx.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
